import * as OrgConfig from "src/generated/hooks/org-config/requests";
import { isNonEmptyString, isNullOrUndefined } from "src/services/predicates";
import z from "zod";

export const MAX_TITLE_LENGTH = 35;
export const MAX_DESCRIPTION_LENGTH = 250;
export const MAX_BUTTON_TEXT_LENGTH = 20;
export const MAX_CUSTOM_CONTENT_LENGTH = 250;

const validateContent = (
  values: StaffContent | FamilyContent,
  ctx: z.RefinementCtx
) => {
  if (values.kind === "custom-content") {
    if (
      !isNonEmptyString(values.plainContent) ||
      !isNonEmptyString(values.content)
    ) {
      ctx.addIssue({
        code: "custom",
        message: `This field is required`,
        path: ["content"],
      });
    }
    if (values.plainContent.length > MAX_CUSTOM_CONTENT_LENGTH) {
      ctx.addIssue({
        code: "custom",
        message: `This field has exceeded maximum characters ${MAX_CUSTOM_CONTENT_LENGTH}`,
        path: ["content"],
      });
    }
  } else {
    if (!isNonEmptyString(values.content)) {
      ctx.addIssue({
        code: "custom",
        message: `This field is required`,
        path: ["content"],
      });
    }

    if (values.content.length > MAX_BUTTON_TEXT_LENGTH) {
      ctx.addIssue({
        code: "custom",
        message: `This field has exceeded maximum characters ${MAX_BUTTON_TEXT_LENGTH}`,
        path: ["content"],
      });
    }
  }
};
const validateTitle = (values: Content, ctx: z.RefinementCtx) => {
  if (values.title.length < 1) {
    ctx.addIssue({
      code: "custom",
      message: "This field is required",
      path: ["title"],
    });
  }
  if (values.title.length > MAX_TITLE_LENGTH) {
    ctx.addIssue({
      code: "custom",
      message: `This field has exceeded maximum characters ${MAX_TITLE_LENGTH}`,
      path: ["title"],
    });
  }
};

const validateHtmlDescription = (values: Content, ctx: z.RefinementCtx) => {
  if (!isNonEmptyString(values.plainDescription)) {
    ctx.addIssue({
      code: "custom",
      message: `This field is required`,
      path: ["description"],
    });
  }
  if (values.plainDescription.length > MAX_DESCRIPTION_LENGTH) {
    ctx.addIssue({
      code: "custom",
      message: `This field has exceeded maximum characters ${MAX_DESCRIPTION_LENGTH}`,
      path: ["description"],
    });
  }
};

const ContentSchema = z.object({
  title: z
    .string()
    .min(1, "This field is required")
    .max(
      MAX_TITLE_LENGTH,
      `This field has exceeded maximum characters ${MAX_TITLE_LENGTH}`
    ),
  plainDescription: z.string(),
  description: z.string(),
});

type Content = z.infer<typeof ContentSchema>;

const CustomContentSchema = z.object({
  kind: z.literal("custom-content"),
  content: z.string(),
  plainContent: z.string(),
});

const FamilyContentSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("family-login-button"),
    content: z.string(),
  }),
  z.object({
    kind: z.literal("family-signup-button"),
    content: z.string(),
  }),
  z.object({
    kind: z.literal("account-lookup-button"),
    content: z.string(),
  }),
  CustomContentSchema,
]);
export type FamilyContent = z.infer<typeof FamilyContentSchema>;

const StaffContentSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("staff-login-button"),
    content: z.string(),
  }),
  CustomContentSchema,
]);
export type StaffContent = z.infer<typeof StaffContentSchema>;

const BannerSchema = z
  .object({
    status: z.union([z.literal("enabled"), z.literal("disabled")]),
    title: z.string(),
    plainDescription: z.string(),
    description: z.string(),
    imageUrl: z.string().nullable(),
    image: z.instanceof(File).nullable(),
  })
  .superRefine((value, ctx) => {
    if (value.status === "disabled") {
      return;
    }

    validateTitle(value, ctx);
    validateHtmlDescription(value, ctx);

    if (isNullOrUndefined(value.image) && isNullOrUndefined(value.imageUrl)) {
      ctx.addIssue({
        code: "custom",
        message: "This field is required",
        path: ["image"],
      });
    }

    if (!isNullOrUndefined(value.image) && value.image.size > 10_000_000) {
      ctx.addIssue({
        code: "custom",
        message: "Image size must be 10 MB or less",
        path: ["image"],
      });
    }
  });

const AdditionalContentSchema = z
  .object({
    status: z.union([z.literal("enabled"), z.literal("disabled")]),
    title: z.string(),
    description: z.string(),
    plainDescription: z.string(),
  })
  .superRefine((values, ctx) => {
    if (values.status === "disabled") {
      return;
    }

    validateTitle(values, ctx);
    validateHtmlDescription(values, ctx);
  });

export const FormikSchema = z.object({
  banner: BannerSchema,
  familyCard: ContentSchema.extend({
    contents: z.array(FamilyContentSchema.superRefine(validateContent)),
  }).superRefine(validateHtmlDescription),
  staffCard: ContentSchema.extend({
    contents: z.array(StaffContentSchema.superRefine(validateContent)),
  }).superRefine(validateHtmlDescription),
  additionalContent: AdditionalContentSchema,
});

export function toFormik(
  config: OrgConfig.LandingPageConfig | null,
  toPlainText: (html: string) => string
): FormikType {
  const familyCard = config?.welcomeCards?.find(
    (card) => card.kind === "family"
  );

  const staffCard = config?.welcomeCards?.find((card) => card.kind === "staff");
  const additionalContent = config?.additionalContents?.[0];
  return {
    banner: toFormikBanner(config?.banner, toPlainText),
    familyCard: toFormikFamilyCard(familyCard, toPlainText),
    staffCard: toFormikStaffCard(staffCard, toPlainText),
    additionalContent: toFormikAdditionalContent(
      additionalContent,
      toPlainText
    ),
  };
}

function toFormikBanner(
  banner: OrgConfig.LandingPageBannerContent | undefined,
  toPlainText: (html: string) => string
): FormikBannerType {
  return {
    status: banner?.enabled ? "enabled" : "disabled",
    imageUrl: banner?.imageUrl ? banner?.imageUrl : null,
    image: null,
    title: banner?.title ?? "",
    description: banner?.description ?? "",
    plainDescription: toPlainText(banner?.description ?? ""),
  };
}

function toFormikFamilyCard(
  card: OrgConfig.LandingPageFamilyCard | undefined,
  toPlainText: (html: string) => string
): FormikFamilyCardType {
  if (isNullOrUndefined(card)) {
    card = DefaultConfig.welcomeCards[0];
  }

  const contents = card.contents.map((content) =>
    toFormikFamilyContent(content, toPlainText)
  );

  if (contents.length === 0) {
    contents.push(...DefaultConfig.welcomeCards[0].contents);
  }

  return {
    title: card.title,
    description: card.description,
    plainDescription: toPlainText(card.description),
    contents,
  };
}

function toFormikFamilyContent(
  content: OrgConfig.LandingPageFamilyContent,
  toPlainText: (html: string) => string
): FormikFamilyContentType {
  if (content.kind === "custom-content") {
    return {
      kind: "custom-content",
      content: content.content,
      plainContent: toPlainText(content.content),
    };
  }
  return {
    kind: content.kind,
    content: content.content,
  };
}

function toFormikStaffCard(
  card: OrgConfig.LandingPageStaffCard | undefined,
  toPlainText: (html: string) => string
): FormikStaffCardType {
  if (isNullOrUndefined(card)) {
    card = DefaultConfig.welcomeCards[1];
  }
  const contents = card.contents.map((content) =>
    toFormikStaffContent(content, toPlainText)
  );
  if (contents.length === 0) {
    contents.push({
      kind: "staff-login-button",
      content: "Staff log in",
    });
  }
  return {
    title: card.title,
    description: card.description,
    plainDescription: toPlainText(card.description),
    contents,
  };
}

function toFormikStaffContent(
  content: OrgConfig.LandingPageStaffContent,
  toPlainText: (html: string) => string
): FormikStaffContentType {
  if (content.kind === "custom-content") {
    return {
      kind: "custom-content",
      content: content.content,
      plainContent: toPlainText(content.content),
    };
  }
  return {
    kind: content.kind,
    content: content.content,
  };
}

function toFormikAdditionalContent(
  content: OrgConfig.LandingPageAdditionalContent | undefined,
  toPlainText: (html: string) => string
): FormikAdditionalContentType {
  return {
    status: content ? "enabled" : "disabled",
    title: content?.title ?? "",
    description: content?.description ?? "",
    plainDescription: toPlainText(content?.description ?? ""),
  };
}

export function fromFormik(
  values: FormikType,
  bannerImageUrl: string
): OrgConfig.LandingPageConfig {
  const banner: OrgConfig.LandingPageConfig["banner"] = {
    enabled: values.banner.status === "enabled",
    title: values.banner.title,
    description: values.banner.description,
    imageUrl: bannerImageUrl,
  };

  let additionalContents;
  if (values.additionalContent.status === "enabled") {
    additionalContents = [
      {
        title: values.additionalContent.title,
        description: values.additionalContent.description,
      },
    ];
  }

  return {
    disabled: false,
    banner,
    welcomeCards: [
      {
        kind: "family",
        ...values.familyCard,
      },
      {
        kind: "staff",
        ...values.staffCard,
      },
    ],
    additionalContents,
  };
}

const DefaultFamilyLoginButtonContent = "Family log in";
const DefaultFamilySignupButtonContent = "New? Create account";
const DefaultAccountLookupButtonContent = "Recover account";
const DefaultStaffLoginButtonContent = "Staff log in";

export const DefaultConfig = {
  welcomeCards: [
    {
      kind: "family",
      title: "Families",
      description:
        '<p class="LexicalEditorTheme__paragraph"><span style="white-space: pre-wrap;">Parents and guardians applying to schools or checking on an application.</span></p>',
      contents: [
        {
          kind: "family-login-button",
          content: DefaultFamilyLoginButtonContent,
        },
        {
          kind: "family-signup-button",
          content: DefaultFamilySignupButtonContent,
        },
      ],
    },
    {
      kind: "staff",
      title: "School & district staff",
      description:
        '<p class="LexicalEditorTheme__paragraph"><span style="white-space: pre-wrap;">Administrators, school leaders, and team members — use your work account.</span></p>',
      contents: [
        { kind: "staff-login-button", content: DefaultStaffLoginButtonContent },
      ],
    },
  ],
} as const satisfies OrgConfig.LandingPageConfig;

export function getDefaultFamilyContent(
  kind: OrgConfig.LandingPageFamilyContent["kind"]
): string {
  switch (kind) {
    case "family-login-button":
      return DefaultFamilyLoginButtonContent;
    case "family-signup-button":
      return DefaultFamilySignupButtonContent;
    case "account-lookup-button":
      return DefaultAccountLookupButtonContent;
  }

  return "";
}

export function getDefaultStaffContent(
  kind: OrgConfig.LandingPageStaffContent["kind"]
): string {
  switch (kind) {
    case "staff-login-button":
      return DefaultStaffLoginButtonContent;
  }

  return "";
}

export type FormikType = z.infer<typeof FormikSchema>;

type FormikBannerType = FormikType["banner"];

type FormikFamilyCardType = FormikType["familyCard"];
type FormikFamilyContentType = FormikFamilyCardType["contents"][number];
export type FamilyContentKind = FormikFamilyContentType["kind"];

type FormikStaffCardType = FormikType["staffCard"];
type FormikStaffContentType = FormikStaffCardType["contents"][number];
export type StaffContentKind = FormikStaffContentType["kind"];

type FormikAdditionalContentType = FormikType["additionalContent"];
